import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { showSnackbar } from '@global/snackbar/snackbar.actions'
import { api } from 'api'

interface HookArgs {
  templateId: string
  datasetId: string
  submissionName: string
}

interface HookReturn {
  saveSubmissionName: (value: string) => void
}

/**
 * Handle the save of editing a submission name from the read-grid.
 * Must be inside of a useEffect and outside of the called function;
 * this is due callbacks passed to React ContentEditable (RCE) not being refreshed
 * internally by RCE and holding onto internal state values of any function that is
 * consumed by a RCE callback.
 * @param args The arguments passed to hook.
 * @param {string} args.templateId The template ID used when saving submission name.
 * @param {string} args.datasetId The Dataset ID used when saving submission name.
 * @param {string} args.submissioName The current submission name; to prevent unnecessary saves on renders.
 * @returns {HookReturn}
 */
export const useSaveOnToggle = ({
  templateId,
  datasetId,
  submissionName,
}: HookArgs): HookReturn => {
  const dispatch = useDispatch()
  const [submissionNameSaved, setSubmissionNameSaved] = useState(null)

  useEffect(() => {
    if (!submissionNameSaved) return
    if (submissionName === submissionNameSaved) return

    if (!templateId) {
      dispatch(
        showSnackbar({
          message: `Something went wrong. Submission name change was not saved. Try Again.`,
          type: 'ERROR',
          duration: 5000,
        }),
      )
      return
    }

    const payload = {
      id: datasetId,
      template_id: templateId,
      identifier: submissionNameSaved,
    }

    async function saveDatasetSubmissionName() {
      try {
        const response = await api.put(`/api/v1/account/dataset/${datasetId}`, payload)

        if (response?.status === 200) {
          dispatch(
            showSnackbar({
              message: 'Submission name saved successfully',
              type: 'SUCCESS',
              duration: 5000,
            }),
          )
          return
        }

        dispatch(
          showSnackbar({
            message: `Something went wrong. Submission name change was not saved. Status Error: ${response?.status}`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      } catch (err) {
        dispatch(
          showSnackbar({
            message: `Something went wrong. Submission name change was not saved. ${err}`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      }
    }

    void saveDatasetSubmissionName()
  }, [submissionName, submissionNameSaved, templateId, datasetId])

  function saveSubmissionName(value) {
    setSubmissionNameSaved(value)
  }

  return {
    saveSubmissionName,
  }
}
